import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnDestroy,
  SimpleChanges,
  ChangeDetectorRef,
  CUSTOM_ELEMENTS_SCHEMA,
  OnChanges,
} from '@angular/core';
import { Subject } from 'rxjs';
import {
  takeUntil,
  debounceTime,
  distinctUntilChanged,
  filter,
} from 'rxjs/operators';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { FindLocationRestService } from '@loup-transload-ui-app/common-services';
import { CommonModule } from '@angular/common';

type ErrorState = 'warning' | 'error';

@Component({
  selector: 'lib-location-external-autocomplete',
  templateUrl: './location-external-autocomplete.component.html',
  imports: [CommonModule, ReactiveFormsModule],
  styleUrl: './location-external-autocomplete.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  standalone: true,
})
export class LocationExternalAutocompleteComponent
  implements OnInit, OnDestroy, OnChanges
{
  inputStringValue: string | '';
  @Input() location: any;
  @Input() roleType: string = null;
  @Input() locationType = 'CITY_STATE';
  @Input() appearance = 'fill';
  @Input() placeHolder: string;
  @Input() isGlobalSearch = true;
  @Input() required = false;
  @Input() disabled: boolean;
  @Input() isFullLocation = false;
  @Input() isLocationAddress = false;
  @Input() isOnlyName = false;
  @Input() showErrors = true;
  @Input() isDrayCarrier = false;
  @Input() isVendor = false;
  @Input() cityStateText = false;
  @Input() cityStateCountryText = false;
  @Input() isCityStateCountry = false;
  @Input() showPostalCode = false;
  @Output() selectedLocation = new EventEmitter<any>();
  @Output() BlurLocation = new EventEmitter<any>();
  listTemplate = (result: any) => {
    if (!result || (!result.name && !result.city)) {
      return `<div>No results found</div>`;
    } else {
      if (this.cityStateCountryText) {
        return `<div>
               <div class="result-item-name">
                ${result.city ? `<span>${result.city}</span>` : ''}
                ${result.state ? ` <span>${result.state}</span>` : ''}
                ${result.country ? ` <span>${result.country}</span>` : ''}
                ${
                  result.postalCode
                    ? `<span>${
                        result.postalCode ? result.postalCode : ''
                      }</span>`
                    : ''
                }
               </div> `;
      } else if (this.roleType === 'CITY_STATE') {
        return `<div>
                 <div class="result-item-name">
                ${result.city ? `<span>${result.city}</span>` : ''}
                ${result.state ? ` <span>${result.state}</span>` : ''}
               </div> `;
      } else if (this.roleType === 'CITY_STATE_COUNTRY') {
        return `<div>
                 <div class="result-item-name">
                  ${result.city ? `<span>${result.city}</span>` : ''} 
                  ${result.state ? `<span>${result.state}</span>` : ''}
                  ${result.country ? `<span>${result.country}</span>` : ''}
                </div> `;
      } else if (this.roleType === 'CITY_STATE_POSTAL') {
        return `<div>
                <div class="result-item-name">
                 ${result.city ? `<span>${result.city}</span>` : ''}
                 ${result.state ? `<span>${result.state}</span>` : ''} 
                 ${result.country ? `<span>${result.country}</span>` : ''}
                 ${result.postalCode ? `<span>${result.postalCode}</span>` : ''}
               </div> `;
      } else {
        return `<div>   
                <div class="result-item-name">
                ${
                  result.externalCloudKey
                    ? result.name + ' (' + result.externalCloudKey + ')'
                    : result.name
                }
                </div>
                 <div class="result-item-address">
                ${
                  result.addressLine1
                    ? `<span>${result.addressLine1}</span>`
                    : ''
                }
                ${
                  result.addressLine2
                    ? `<span>${result.addressLine2}</span>`
                    : ''
                }
                ${
                  result.addressLine1 || result.addressLine2
                    ? `<span>,</span>`
                    : ''
                }
                ${result.city ? `<span>${result.city}</span>` : ''}
                ${result.state ? ` <span>${result.state}</span>` : ''}
                ${result.postalCode ? `<span>${result.postalCode}</span>` : ''}
                ${result.country ? `<span>${result.country}</span>` : ''}
              </div>
           </div> `;
      }
    }
  };
  formatter = (item: any): string => {
    return this.getValue(item) ?? '';
  };
  locationAutoCompleteState: ErrorState;
  filteredPickupOptions = [];
  locationAutoCompleteMessage = '';
  isActive = false;
  selectedCity = '';
  results: any[] = [];
  observable: any;
  @Input() isSubmitted: boolean;
  private searchTerms = new Subject<string>();
  private unsubscribe$ = new Subject();
  @Input() hasCustomError = false;
  locationControl: FormControl;
  selected: any;

  constructor(
    private locationRestService: FindLocationRestService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.locationControl = new FormControl({
      value: this.inputStringValue,
      disabled: this.disabled,
    });
    if (this.location) {
      this.inputStringValue = this.cityStateText
        ? this.getCityState(this.location)
        : this.getValue(this.location);
    }
    this.observable = this.searchTerms.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      filter((value) => value && value.length > 1)
    );

    this.observable.pipe(takeUntil(this.unsubscribe$)).subscribe((data) => {
      if (this.isDrayCarrier) {
        this.onSearchDrayCarrierLocation(data);
      } else if (this.isVendor) {
        this.onSearchVendorLocation(data);
      } else {
        this.onSearchLocation(data);
      }
    });

    if (!this.roleType) {
      throw new Error('[roleType] required');
    } else if (this.roleType === 'CARRIER') {
      if (Number(sessionStorage.getItem('carrierId')) > 0) {
        const carrierDetails: any = JSON.parse(
          sessionStorage.getItem('selectedCarrier')
        );
        this.inputStringValue = carrierDetails.name;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isSubmitted'] && changes['isSubmitted'].currentValue === true) {
      if (this.location) {
        this.inputStringValue = this.cityStateText ? this.getCityState(this.location) : this.getValue(this.location);
      }
      this.validateAutocomplete();
    }
    if (changes['showErrors']) {
      this.showErrors = changes['showErrors'].currentValue;
      if (!changes['showErrors'].currentValue) {
        this.validateAutocomplete();
      }
    }
    if (
      !this.isSubmitted && this.location && this.location?.id && !this.showErrors) {
      this.inputStringValue = this.cityStateText  ? this.getCityState(this.location) : this.getValue(this.location);
      this.locationControl?.setValue(this.inputStringValue);
    }
    if (changes['location'] && this.location) {
      this.inputStringValue = this.cityStateText ? this.getCityState(this.location) : this.getValue(this.location);
      this.locationControl?.setValue(this.inputStringValue);
      this.cdr.detectChanges();
    }
  }

  getValue(location) {
    if (Object.keys(location).length === 0) {
      return null;
    }
    if (this.cityStateCountryText) {
      return this.getCityState(location);
    }
    if (this.isFullLocation || location.locationType === 'CUSTOMER') {
      return `${this.getFullLocation(location)}`;
    }
    if (this.isLocationAddress) {
      return `${this.getLocationAddress(location)}`;
    }
    if (
      this.isOnlyName ||
      location.locationType === 'POSTAL_CODE' ||
      location.locationType === 'POSTAL_CODE_ALIAS'
    ) {
      return `${location.name}`;
    }
    if (this.isCityStateCountry || this.roleType === 'SHIP_FROM/SHIP_TO') {
      const address = `${this.getShipperAddress(location)}`;
      this.inputStringValue = address;
      return address;
    } else {
      return `${location.name}`;
    }
  }

  getShipperAddress(location: any) {
    if (!location) {
      return '';
    }

    const name = [];
    if (location.name) {
      name.push(location.name);
    }
    if (location.city) {
      name.push(location.city);
    }
    if (location.state) {
      name.push(location.state);
    }
    if (location.country) {
      name.push(location.country);
    }

    return name.join(', ');
  }

  getFullLocation(location: any) {
    if (!location) {
      return '';
    }

    const name = [];
    if (location.name) {
      name.push(location.name);
    }
    if (location.addressLine1) {
      name.push(location.addressLine1);
    }
    if (location.addressLine2) {
      name.push(location.addressLine2);
    }
    if (location.city) {
      name.push(location.city);
    }
    if (location.state) {
      name.push(location.state);
    }
    if (location.country) {
      name.push(location.country);
    }
    return name.join(', ');
  }

  getLocationAddress(location: any) {
    if (!location) {
      return '';
    }
    const name = [];
    if (location.addressLine1) {
      name.push(location.addressLine1);
    }
    if (location.addressLine2) {
      name.push(location.addressLine2);
    }
    if (location.city) {
      name.push(location.city);
    }
    if (location.state) {
      name.push(location.state);
    }
    if (location.country) {
      name.push(location.country);
    }
    if (location.postalCode) {
      name.push(location.postalCode);
    }
    return name.join(', ');
  }

  triggerRestSearch(term: string): void {
    if (term) {
      this.searchTerms.next(term);
    } else {
      this.selectedLocation.emit(null);
      this.searchTerms.next(null);
      this.results = null;
    }
  }

  changeValue(event: Event) {
    if ((event.target as HTMLInputElement).value) {
      this.searchTerms.next((event.target as HTMLInputElement).value);
    } else {
      this.searchTerms.next(null);
    }
  }

  reset() {
    this.locationAutoCompleteState = null;
    this.locationAutoCompleteMessage = '';
    this.inputStringValue = '';
    this.results = [];
    this.filteredPickupOptions = [];
    this.searchTerms.next(null);
    this.selectedLocation.emit(null);
  }

  onSearchLocation(event) {
    this.locationRestService
      .getExternalLocations(event, this.locationType)
      .subscribe((result: any) => {
        if (result.errors && result.errors.length > 0) {
          return;
        }
        if (result.length === 0) {
          this.results = [{}];
          return;
        }
        if (this.roleType === 'CARRIER' && result.data.length === 1) {
          this.inputStringValue = result.data[0].name;
          this.selectedLocation.emit(result.data[0]);
        }
        this.results = result;
      });
    }

  onSearchDrayCarrierLocation(event) {
    this.locationRestService
      .getExternalLocations(event, this.locationType)
      .subscribe((result: any) => {
        if (result.errors && result.errors.length > 0) {
          return;
        }
        if (result.length === 0) {
          this.results = [{}];
          return;
        }
        if (this.roleType === 'CARRIER' && result.data.length === 1) {
          this.inputStringValue = result.data[0].name;
          this.selectedLocation.emit(result.data[0]);
        }
        this.results = result;
      });
  }

  onSearchVendorLocation(event) {
    this.locationRestService
      .getExternalLocations(event, this.locationType)
      .subscribe((result: any) => {
        if (result.errors && result.errors.length > 0) {
          return;
        }
        if (result.length === 0) {
          this.results = [{}];
          return;
        }

        if (this.roleType === 'VENDOR' && result.data.length === 1) {
          this.inputStringValue = result.data[0].name;
        }

        this.results = result;
      });
  }

  onLocationSelect(locationObj: any) {
    const location = locationObj ? locationObj.detail || locationObj : {};
    this.inputStringValue = this.cityStateText
      ? this.getCityState(location)
      : this.getValue(location);
    this.locationControl.setValue(this.inputStringValue);
    if (location && Object.keys(location).length === 0) {
      this.selectedLocation.emit(null);
      return;
    }
    this.selectedLocation.emit(location);
    this.selected = location;
    this.cdr.detectChanges();
  }

  getCityState(result) {
    return (
      `${result?.city ? result.city : ''}${
        result?.state ? ' ' + result.state : ''
      }` +
      `${
        this.cityStateCountryText && result?.country
          ? ' ' + result.country
          : ''
      }` +
      `${
        this.showPostalCode && result?.postalCode
          ? ' ' + result.postalCode
          : ''
      }`
    );
  }

  validateAutocomplete() {
    this.locationAutoCompleteState = null;
    this.locationAutoCompleteMessage = '';
    if (this.hasCustomError && this.inputStringValue) {
      this.locationAutoCompleteState = this.isSubmitted ? 'error' : 'warning';
    }
    if (this.showErrors && !this.inputStringValue) {
      this.locationAutoCompleteState = this.isSubmitted ? 'error' : 'warning';
      this.locationAutoCompleteMessage = this.placeHolder + ' is required.';
    }
  }

  onBlur(selectedValue: string) {
    if(!selectedValue){
     this.inputStringValue = null;
    }
    this.validateAutocomplete();
    this.BlurLocation.emit(this.selected);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.complete();
  }
}
